const normalizeStatusToken = (value: string | null | undefined): string =>
  (value || '')
    .trim()
    .toUpperCase()
    .replace(/-/g, '_')
    .replace(/\s+/g, '_')

/** Normalize raw API sync/shipment status for display and badge styling. */
export const normalizeSyncStatus = (status: string | null | undefined): string => {
  const normalized = normalizeStatusToken(status)
  if (!normalized) return ''
  if (normalized === 'INCOMLETED' || normalized === 'INCOMPLETED') return 'INCOMPLETE'
  return normalized
}

const SYNC_STATUS_LABELS: Record<string, string> = {
  ERROR: 'Failed',
  FAILED: 'Failed',
  PENDING: 'Pending',
  OPEN: 'Open',
  SYNCED: 'Synced',
  SUCCESS: 'Success',
  FINISHED: 'Finished',
  ERROR_UNLINKED: 'Failed',
  SYNCED_PARTIALLY: 'Partially Synced',
  SYNCED_WITH_ERROR: 'Sync with error',
  ERROR_NO_MAPPING: 'Failed',
  PENDING_APPROVAL: 'Pending Approval',
  PROCESSING: 'Processing',
  INCOMPLETE: 'Incomplete',
  ONHOLD: 'Onhold',
  IGNORED: 'Ignored',
  INSERT: 'Insert',
  UPDATE: 'Update',
  DELETE: 'Delete',
  DELISTED: 'Delisted',
  ORDER_RESYNC: 'Order Resync',
  PRODUCT_RESYNC: 'Product Resync',
  REFRESH_MAPPING: 'Refresh Mapping',
  OTHER: 'other',
  SHIPPED: 'Shipped',
  DELIVERED: 'Delivered',
  PARTIALLY_SHIPPED: 'Partially Shipped',
  NA: 'N/A',
  'N/A': 'N/A',
}

export const formatUnknownSyncStatusLabel = (normalized: string): string =>
  normalized
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ')

/** Human-readable label; empty input → N/A; unknown non-empty → title case from token. */
export const formatSyncStatusLabel = (status: string | null | undefined): string => {
  const normalized = normalizeSyncStatus(status)
  if (!normalized) return 'N/A'
  return SYNC_STATUS_LABELS[normalized] ?? formatUnknownSyncStatusLabel(normalized)
}

const ERROR_POPOVER_STATUSES = new Set([
  'ERROR',
  'FAILED',
  'ERROR_UNLINKED',
  'ERROR_NO_MAPPING',
  'SYNCED_WITH_ERROR',
  'DELISTED',
])

export const shouldShowSyncStatusPopover = (normalizedStatus: string): boolean =>
  ERROR_POPOVER_STATUSES.has(normalizedStatus)

export const getSyncStatusBadgeBackgroundColor = (normalizedStatus: string): string => {
  if (normalizedStatus === 'SYNCED_PARTIALLY') return 'lightsteelblue'
  if (normalizedStatus === 'SYNCED_WITH_ERROR') return 'red'
  if (normalizedStatus === 'PENDING_APPROVAL') return '#F9F9A0'
  if (normalizedStatus === 'ERROR' || normalizedStatus === 'FAILED') return 'red'
  if (normalizedStatus === 'DELETE') return 'red'
  if (normalizedStatus === 'DELISTED') return '#6c757d'
  if (normalizedStatus === 'PENDING' || normalizedStatus === 'OPEN') return '#FFD700'
  if (normalizedStatus === 'SYNCED' || normalizedStatus === 'SHIPPED') return 'rgb(25, 135, 84)'
  if (normalizedStatus === 'PROCESSING' || normalizedStatus === 'UPDATE') return '#003E80'
  if (normalizedStatus === 'SUCCESS' || normalizedStatus === 'FINISHED' || normalizedStatus === 'DELIVERED') {
    return 'rgb(25, 135, 84)'
  }
  if (normalizedStatus === 'INSERT') return 'rgb(25, 135, 84)'
  if (normalizedStatus === 'ERROR_UNLINKED' || normalizedStatus === 'ERROR_NO_MAPPING') return 'red'
  if (
    normalizedStatus === 'ONHOLD' ||
    normalizedStatus === 'IGNORED' ||
    normalizedStatus === 'OTHER' ||
    normalizedStatus === 'NA'
  ) {
    return 'darkslategray'
  }
  if (normalizedStatus === 'INCOMPLETE') return '#FFA500'
  if (['ORDER_RESYNC', 'PRODUCT_RESYNC', 'REFRESH_MAPPING'].includes(normalizedStatus)) {
    return 'darkslategray'
  }
  if (normalizedStatus === 'PARTIALLY_SHIPPED') return 'lightsteelblue'
  return 'darkslategray'
}
